import { useCallback, useState, useEffect } from "react";
import {
    useWeb3Modal,
    useWeb3ModalAccount,
    useWeb3ModalProvider,
    useDisconnect,
    useWeb3ModalState,
} from "@web3modal/ethers5/react";
import { ethers } from "ethers";
import { SONIC_CHAIN_ID, SONIC_CHAIN_CONFIG } from "../blockchain/contracts";

export const useWallet = () => {
    const { open } = useWeb3Modal();
    const { address, chainId, isConnected } = useWeb3ModalAccount();
    const { walletProvider } = useWeb3ModalProvider();
    const { disconnect: web3Disconnect } = useDisconnect();
    const { open: isModalOpen } = useWeb3ModalState();

    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionError, setConnectionError] = useState(null);

    const isCorrectNetwork = chainId === SONIC_CHAIN_ID;

    // Track connection state based on modal and connection status
    useEffect(() => {
        if (isConnected) {
            setIsConnecting(false);
            setConnectionError(null);
        }
    }, [isConnected]);

    // Reset connecting state when modal closes without connection
    useEffect(() => {
        if (!isModalOpen && !isConnected && isConnecting) {
            // Give a small delay for mobile wallets that may take longer
            const timeout = setTimeout(() => {
                if (!isConnected) {
                    setIsConnecting(false);
                }
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [isModalOpen, isConnected, isConnecting]);

    // Get ethers provider and signer
    const getProvider = useCallback(() => {
        if (!walletProvider) return null;
        return new ethers.providers.Web3Provider(walletProvider);
    }, [walletProvider]);

    const getSigner = useCallback(() => {
        const provider = getProvider();
        if (!provider) return null;
        return provider.getSigner();
    }, [getProvider]);

    // Connect wallet - opens Web3Modal
    const connect = useCallback(async () => {
        try {
            setIsConnecting(true);
            setConnectionError(null);
            await open();
        } catch (error) {
            console.error("Connection error:", error);
            setConnectionError(error.message || "Failed to connect wallet");
            setIsConnecting(false);
        }
    }, [open]);

    // Disconnect wallet
    const disconnect = useCallback(async () => {
        try {
            await web3Disconnect();
            setConnectionError(null);
        } catch (error) {
            console.error("Disconnect error:", error);
        }
    }, [web3Disconnect]);

    // Switch to Sonic network
    const switchToSonic = useCallback(async () => {
        if (!walletProvider) return;

        try {
            await walletProvider.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: SONIC_CHAIN_CONFIG.chainId }],
            });
        } catch (switchError) {
            // Chain not added, add it
            if (switchError.code === 4902) {
                try {
                    await walletProvider.request({
                        method: "wallet_addEthereumChain",
                        params: [SONIC_CHAIN_CONFIG],
                    });
                } catch (addError) {
                    console.error("Failed to add Sonic network:", addError);
                    throw addError;
                }
            } else {
                throw switchError;
            }
        }
    }, [walletProvider]);

    const formatAddress = (addr) => {
        if (!addr) return "";
        return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    };

    return {
        address,
        provider: getProvider(),
        signer: getSigner(),
        isConnecting,
        isConnected,
        isCorrectNetwork,
        chainId,
        error: connectionError,
        connect,
        disconnect,
        switchToSonic,
        formatAddress,
        // These are no longer needed with Web3Modal but kept for compatibility
        availableWallets: [],
        showWalletModal: false,
        closeWalletModal: () => {},
        connectWallet: () => {},
    };
};

export default useWallet;
